/**
 * EB 菜单树 → RuoYi 侧栏路由结构（feat/eb-shell）
 */
import type { RouteLocationNormalized } from 'vue-router';
import  {roterPre} from "@/setting"

export interface CrmebMenuNode {
  id?: number;
  pid?: number;
  path: string;
  title?: string;
  menuName?: string;
  icon?: string;
  auth?: string[];
  children?: CrmebMenuNode[];
}

/** EB 分栏/侧栏 el-menu 节点（path 含 /merchant 前缀） */
export interface CrmebNavMenuItem {
  path: string;
  title: string;
  icon?: string;
  children?: CrmebNavMenuItem[];
  k?: number;
}

export function crmebNodesToNavList(
  menus: CrmebMenuNode[],
  routePrefix = roterPre,
): CrmebNavMenuItem[] {
  return (menus || [])
    .filter((m) => m.path)
    .map((m) => {
      const path = ensureCrmebPrefix(m.path || '', routePrefix);
      const children = m.children?.length
        ? crmebNodesToNavList(m.children, routePrefix)
        : undefined;
      return {
        path,
        title: m.title || m.menuName || '',
        icon: m.icon || '',
        children: children?.length ? children : undefined,
      };
    });
}


/** 规范为带 /admin 前缀的路由 path（菜单、router-link 统一） */
export function ensureCrmebPrefix(path: string, prefix = roterPre): string {
  if (!path) return prefix || '/';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (prefix && !normalized.startsWith(prefix)) {
    return `${prefix}${normalized}`;
  }
  return normalized;
}

/** 页面内 router-link / router.push */
export function crmebPath(path: string, prefix = roterPre): string {
  return ensureCrmebPrefix(path, prefix);
}

export function findFirstMenuUrl(menus: CrmebMenuNode[] | null | undefined): string {
  let url = '/';
  let cur = menus;
  while (cur && cur.length) {
    const menu = cur[0];
    url = ensureCrmebPrefix(menu.path || '/');
    cur = menu.children;
  }
  return url;
}

/** 将 EB 菜单转为现有 Sidebar 组件可用的路由节点 */
export function crmebMenuToSidebarRoutes(
  menus: CrmebMenuNode[],
  routePrefix = roterPre,
): any[] {
  return (menus || []).map((m) => {
      const path = ensureCrmebPrefix(m.path || '', routePrefix);
    const children = m.children?.length
      ? crmebMenuToSidebarRoutes(m.children, routePrefix)
      : undefined;
    const safeName = `CrmebMenu_${m.id ?? path.replace(/[^\w]+/g, '_')}`;
    return {
      path,
      name: safeName,
      meta: {
        title: m.title || m.menuName || '',
        icon: m.icon || '',
      },
      children,
      hidden: false,
    };
  });
}

/** 菜单 auth 与 access 交集（对齐 EB includeArray） */
export function includeArray(list: string[] | undefined, access: string[] | undefined): boolean {
  if (!list?.length) return true;
  if (!access?.length) return false;
  return list.some((item) => access.includes(item));
}

export function filterMenuByAccess(
  menuList: CrmebMenuNode[],
  access: string[],
): CrmebMenuNode[] {
  const result: CrmebMenuNode[] = [];
  (menuList || []).forEach((menu) => {
    const menuAuth = menu.auth;
    if (!menuAuth?.length || includeArray(menuAuth, access)) {
      const node: CrmebMenuNode = { ...menu };
      if (menu.children?.length) {
        node.children = filterMenuByAccess(menu.children, access);
      } else {
        delete node.children;
      }
      result.push(node);
    }
  });
  return result;
}

/** 扁平化侧栏菜单，供顶栏菜单搜索 */
/** 表单子页标题（对齐 EB menu.js getFormTitle） */
export function getCrmebFormPageTitle(route: RouteLocationNormalized): string {
  const segment = route.path.split('/').filter(Boolean).pop() || '';
  switch (segment) {
    case 'mer_base':
      return '店铺配置';
    case 'svip':
      return '付费会员设置';
    default:
      return String(route.meta?.title || '当前页');
  }
}

function flattenMenuTree(menus: CrmebMenuNode[]): CrmebMenuNode[] {
  const list: CrmebMenuNode[] = [];
  const walk = (nodes: CrmebMenuNode[]) => {
    nodes.forEach((node) => {
      list.push(node);
      if (node.children?.length) walk(node.children);
    });
  };
  walk(menus || []);
  return list;
}

/** 面包屑按 path 去重（保留首次出现顺序，同 path 以后者 title 为准） */
export function dedupeBreadcrumbByPath(
  items: Array<{ path: string; title: string }>,
): Array<{ path: string; title: string }> {
  const pathToItem = new Map<string, { path: string; title: string }>();
  const order: string[] = [];
  for (const item of items) {
    if (!item.title) continue;
    if (!pathToItem.has(item.path)) {
      order.push(item.path);
    }
    pathToItem.set(item.path, item);
  }
  return order.map((path) => pathToItem.get(path)!);
}

/** 按菜单树与当前 path 生成面包屑（优先于 route.matched） */
export function buildMenuBreadcrumbChain(
  menuTree: CrmebMenuNode[],
  targetPath: string,
): Array<{ path: string; title: string }> {
  const flat = flattenMenuTree(menuTree);
  if (!flat.length) return [];
  const normalized = ensureCrmebPrefix(targetPath);
  let current = flat.find((m) => ensureCrmebPrefix(m.path || '') === normalized);
  if (!current) {
    current = flat.find((m) => normalized.startsWith(ensureCrmebPrefix(m.path || '')));
  }
  if (!current) return [];

  const chain: Array<{ path: string; title: string }> = [];
  const byId = new Map<number, CrmebMenuNode>();
  flat.forEach((m) => {
    if (m.id != null) byId.set(m.id, m);
  });

  const visitedIds = new Set<number>();
  let node: CrmebMenuNode | undefined = current;
  while (node) {
    if (node.id != null) {
      if (visitedIds.has(node.id)) break;
      visitedIds.add(node.id);
    }
    chain.unshift({
      path: ensureCrmebPrefix(node.path || ''),
      title: node.title || node.menuName || '',
    });
    if (node.pid == null) break;
    node = byId.get(node.pid);
  }
  return dedupeBreadcrumbByPath(chain);
}

export function flattenCrmebNavMenus(
  menus: CrmebNavMenuItem[],
): Array<{ path: string; title: string; icon?: string }> {
  const list: Array<{ path: string; title: string; icon?: string }> = [];
  const walk = (items: CrmebNavMenuItem[]) => {
    items.forEach((item) => {
      if (item.children?.length) {
        walk(item.children);
      } else if (item.path) {
        list.push({
          path: item.path,
          title: item.title || '',
          icon: item.icon,
        });
      }
    });
  };
  walk(menus || []);
  return list;
}
